import React, { useState } from 'react';
import { Building, Copy, Grid3X3, GripVertical, Rows3 } from 'lucide-react';
import { Unidade, Torre, UnitRef, RowRef, inputStyle, primaryButtonStyle, successButtonStyle, dangerButtonStyle } from './types';

interface TorreViewProps {
    torre: Torre;
    activeTab: number;
    geracaoEmMassa: { andares: string; unidadesPorAndar: string };
    setGeracaoEmMassa: (value: { andares: string; unidadesPorAndar: string }) => void;
    handleGeracaoEmMassa: () => void;
    selectedUnits: Map<string, UnitRef>;
    sourceCoords: UnitRef | null;
    handleSetSource: () => void;
    handleSelectAllInTower: () => void;
    handleApplyToSelected: () => void;
    handleClearSelection: () => void;
    validateUnidade: (unidade: Unidade) => { isValid: boolean; isPartial: boolean };
    handleCellClick: (isSource: boolean, unitKey: string, coords: UnitRef) => void;
    handleUnitSelection: (key: string, coords: UnitRef) => void;
    removeUnidade: (andarIndex: number, unidadeIndex: number) => void;
    getColCount: (colIndex: number) => number;
    getRowCount: (rowIndex: number) => number;
    addUnidadeToAndar: (andarIndex: number) => void;
    removeAndar: (andarIndex: number) => void;
    addAndar: () => void;
    // Seleção em bloco e modelo por linha
    handleSelectRow: (andarIndex: number) => void;
    handleSelectColumn: (unidadeIndex: number) => void;
    sourceRow: RowRef | null;
    selectedRows: Set<number>;
    handleSetSourceRow: (andarIndex: number) => void;
    handleToggleTargetRow: (andarIndex: number) => void;
    handleSelectAllRowsInTower: () => void;
    handleApplyRowToSelected: () => void;
    // Reordenação de andares
    moveAndar: (from: number, to: number) => void;
    // Arraste de unidades individuais (troca ou move para célula vazia)
    moveUnidadeCell: (from: UnitRef, to: UnitRef) => void;
    // Renomeação de pavimento
    updatePavimentoTexto: (andarIndex: number, novoTexto: string) => void;
}

const TorreView: React.FC<TorreViewProps> = ({
    torre,
    activeTab,
    geracaoEmMassa,
    setGeracaoEmMassa,
    handleGeracaoEmMassa,
    selectedUnits,
    sourceCoords,
    handleSetSource,
    handleSelectAllInTower,
    handleApplyToSelected,
    handleClearSelection,
    validateUnidade,
    handleCellClick,
    handleUnitSelection,
    removeUnidade,
    getColCount,
    getRowCount,
    addUnidadeToAndar,
    removeAndar,
    addAndar,
    handleSelectRow,
    handleSelectColumn,
    sourceRow,
    selectedRows,
    handleSetSourceRow,
    handleToggleTargetRow,
    handleSelectAllRowsInTower,
    handleApplyRowToSelected,
    moveAndar,
    moveUnidadeCell,
    updatePavimentoTexto,
}) => {
    // Índices só para o feedback visual do arraste; o move acontece no drop.
    const [dragRow, setDragRow] = useState<number | null>(null);
    const [overRow, setOverRow] = useState<number | null>(null);
    const [dragUnit, setDragUnit] = useState<UnitRef | null>(null);
    const [overUnitKey, setOverUnitKey] = useState<string | null>(null);
    const [editandoAndarIndice, setEditandoAndarIndice] = useState<number | null>(null);
    const [novoNomePavimento, setNovoNomePavimento] = useState<string>("");

    const totalColunas = Math.max(0, ...(torre?.unidades.map(row => row.length) || [0]));
    const modoLinha = !!sourceRow;

    const handleConfirmarRenomeioPavimento = (andarIndex: number) => {
        updatePavimentoTexto(andarIndex, novoNomePavimento.trim());
        setEditandoAndarIndice(null);
        setNovoNomePavimento("");
    };

    const handleKeyDownPavimento = (e: React.KeyboardEvent, andarIndex: number) => {
        if (e.key === "Enter") {
            handleConfirmarRenomeioPavimento(andarIndex);
        } else if (e.key === "Escape") {
            setEditandoAndarIndice(null);
            setNovoNomePavimento("");
        }
    };

    return (
        <>
            <div className="my-6 p-4 border rounded-lg bg-gray-50 shadow-sm">
                <h4 className="text-md font-semibold text-gray-800 mb-3">Geração Rápida de Unidades</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nº de Andares</label>
                        <input
                            type="number"
                            value={geracaoEmMassa.andares}
                            onChange={(e) => setGeracaoEmMassa({ ...geracaoEmMassa, andares: e.target.value })}
                            className={inputStyle}
                            placeholder="Ex: 20"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Unidades por Andar</label>
                        <input
                            type="number"
                            value={geracaoEmMassa.unidadesPorAndar}
                            onChange={(e) => setGeracaoEmMassa({ ...geracaoEmMassa, unidadesPorAndar: e.target.value })}
                            className={inputStyle}
                            placeholder="Ex: 4"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={handleGeracaoEmMassa}
                        className={`${primaryButtonStyle} h-10 flex items-center justify-center gap-2`}
                    >
                        <Grid3X3 size={16} />
                        Gerar Grade
                    </button>
                </div>
            </div>

            {modoLinha && (
                <div className="my-4 p-3 bg-amber-50 rounded-md flex flex-wrap items-center gap-3 border border-amber-300">
                    <p className="font-semibold text-gray-800 flex items-center gap-2">
                        <Rows3 size={16} />
                        Linha modelo: {torre.unidades.length - (sourceRow?.andarIndex ?? 0)}º andar
                        <span className="font-normal text-gray-600">
                            ({torre.unidades[sourceRow!.andarIndex]?.length ?? 0} unid.)
                        </span>
                    </p>
                    <span className="text-xs text-gray-600">
                        Clique nos andares de destino. Eles ficarão com exatamente as mesmas unidades do modelo.
                    </span>
                    <button
                        type="button"
                        onClick={handleSelectAllRowsInTower}
                        className="px-3 py-1 bg-blue-500 text-white text-xs font-bold rounded-md"
                    >
                        Selecionar Todos os Andares
                    </button>
                    <button
                        type="button"
                        onClick={handleApplyRowToSelected}
                        disabled={selectedRows.size === 0}
                        className="px-3 py-1 bg-green-600 text-white text-xs font-bold rounded-md disabled:bg-gray-400"
                    >
                        Aplicar para {selectedRows.size} andar(es)
                    </button>
                    <button type="button" onClick={handleClearSelection} className="px-3 py-1 bg-red-500 text-white text-xs font-bold rounded-md">
                        Cancelar
                    </button>
                </div>
            )}

            {!modoLinha && (selectedUnits.size > 0 || sourceCoords) && (
                <div className="my-4 p-3 bg-gray-100 rounded-md flex flex-wrap items-center gap-3 border border-gray-200">
                    <p className="font-semibold text-gray-800">Ações de Cópia:</p>
                    {!sourceCoords ? (
                        <button
                            type="button"
                            onClick={handleSetSource}
                            disabled={selectedUnits.size !== 1}
                            className="px-3 py-1 bg-blue-500 text-white text-xs font-bold rounded-md disabled:bg-gray-400 flex items-center gap-2"
                        >
                            <Copy size={14} /> Usar como Modelo
                        </button>
                    ) : (
                        <>
                            <button
                                type="button"
                                onClick={handleSelectAllInTower}
                                className="px-3 py-1 bg-blue-500 text-white text-xs font-bold rounded-md flex items-center gap-2"
                            >
                                Selecionar Todas da Torre
                            </button>
                            <button
                                type="button"
                                onClick={handleApplyToSelected}
                                disabled={selectedUnits.size === 0}
                                className="px-3 py-1 bg-green-600 text-white text-xs font-bold rounded-md disabled:bg-gray-400"
                            >
                                Aplicar para {selectedUnits.size} selecionados
                            </button>
                        </>
                    )}
                    <button type="button" onClick={handleClearSelection} className="px-3 py-1 bg-red-500 text-white text-xs font-bold rounded-md">
                        Limpar Seleção
                    </button>
                </div>
            )}

            {torre?.unidades.length > 0 ? (
                <div className="overflow-x-auto border rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Andar</th>
                                {Array.from({ length: totalColunas }).map((_, col) => (
                                    <th
                                        key={col}
                                        onClick={() => !modoLinha && handleSelectColumn(col)}
                                        title={modoLinha ? undefined : 'Clique para selecionar a coluna inteira'}
                                        className={`px-4 py-3 text-center text-xs font-bold text-gray-600 uppercase tracking-wider ${
                                            modoLinha ? '' : 'cursor-pointer hover:bg-indigo-100 hover:text-indigo-700'
                                        }`}
                                    >
                                        Unid. {col + 1} ({getColCount(col)})
                                    </th>
                                ))}
                                <th className="px-4 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {torre.unidades.map((row, rowIndex) => {
                                const totalAndares = torre.unidades.length;
                                const andarDisplay = totalAndares - rowIndex;
                                const isSourceRow = sourceRow?.torreIndex === activeTab && sourceRow?.andarIndex === rowIndex;
                                const isTargetRow = selectedRows.has(rowIndex);
                                const isDragging = dragRow === rowIndex;
                                const isOver = overRow === rowIndex && dragRow !== rowIndex;

                                let rowStyle = 'hover:bg-gray-50';
                                if (isSourceRow) rowStyle = 'bg-amber-100';
                                else if (isTargetRow) rowStyle = 'bg-blue-50';
                                if (isDragging) rowStyle += ' opacity-40';
                                if (isOver) rowStyle += ' ring-2 ring-inset ring-indigo-400';

                                return (
                                    <tr
                                        key={rowIndex}
                                        onDragOver={(e) => {
                                            if (dragRow === null) return;
                                            e.preventDefault();
                                            e.dataTransfer.dropEffect = 'move';
                                            setOverRow(rowIndex);
                                        }}
                                        onDragLeave={() => setOverRow((atual) => (atual === rowIndex ? null : atual))}
                                        onDrop={(e) => {
                                            if (dragRow === null) return;
                                            e.preventDefault();
                                            moveAndar(dragRow, rowIndex);
                                            setDragRow(null);
                                            setOverRow(null);
                                        }}
                                        className={rowStyle}
                                    >
                                        <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-800">
                                            <div className="flex items-center gap-2">
                                                <span
                                                    draggable
                                                    onDragStart={(e) => {
                                                        setDragRow(rowIndex);
                                                        e.dataTransfer.effectAllowed = 'move';
                                                    }}
                                                    onDragEnd={() => {
                                                        setDragRow(null);
                                                        setOverRow(null);
                                                    }}
                                                    title="Arraste para reordenar os andares"
                                                    className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
                                                >
                                                    <GripVertical size={16} />
                                                </span>
                                                {editandoAndarIndice === rowIndex ? (
                                                    <input
                                                        autoFocus
                                                        type="text"
                                                        value={novoNomePavimento}
                                                        onChange={(e) => setNovoNomePavimento(e.target.value)}
                                                        onBlur={() => handleConfirmarRenomeioPavimento(rowIndex)}
                                                        onKeyDown={(e) => handleKeyDownPavimento(e, rowIndex)}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="px-2 py-1 border border-indigo-500 rounded text-sm font-medium text-gray-800 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                                        placeholder="Nome do pavimento"
                                                    />
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            if (modoLinha) {
                                                                handleToggleTargetRow(rowIndex);
                                                            } else {
                                                                handleSelectRow(rowIndex);
                                                                setEditandoAndarIndice(rowIndex);
                                                                setNovoNomePavimento(
                                                                    torre.unidades[rowIndex]?.[0]?.pavimento_texto || `${andarDisplay}º Andar`
                                                                );
                                                            }
                                                        }}
                                                        title={modoLinha ? 'Clique para marcar como destino' : 'Clique para editar o nome do pavimento'}
                                                        className="text-left hover:text-indigo-700 transition-colors"
                                                    >
                                                        {torre.unidades[rowIndex]?.[0]?.pavimento_texto || `${andarDisplay}º Andar`} ({getRowCount(rowIndex)})
                                                        {isSourceRow && <span className="ml-2 text-xs font-bold text-amber-700">MODELO</span>}
                                                        {isTargetRow && <span className="ml-2 text-xs font-bold text-blue-700">DESTINO</span>}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                        {Array.from({ length: totalColunas }).map((_, colIndex) => {
                                            const ref: UnitRef = { type: 'torre', torreIndex: activeTab, andarIndex: rowIndex, unidadeIndex: colIndex };
                                            const unitKey = `t${ref.torreIndex}-a${ref.andarIndex}-u${ref.unidadeIndex}`;
                                            const isSelected = selectedUnits.has(unitKey);
                                            const isSource = sourceCoords?.type === 'torre' && sourceCoords.torreIndex === ref.torreIndex && sourceCoords.andarIndex === ref.andarIndex && sourceCoords.unidadeIndex === ref.unidadeIndex;
                                            const isDraggingThis = dragUnit?.type === 'torre' && dragUnit.torreIndex === ref.torreIndex && dragUnit.andarIndex === ref.andarIndex && dragUnit.unidadeIndex === ref.unidadeIndex;
                                            const isDropTarget = !!dragUnit && overUnitKey === unitKey && !isDraggingThis;

                                            const dragHandlers = modoLinha ? {} : {
                                                onDragOver: (e: React.DragEvent) => {
                                                    if (!dragUnit || isDraggingThis) return;
                                                    e.preventDefault();
                                                    e.dataTransfer.dropEffect = 'move';
                                                    setOverUnitKey(unitKey);
                                                },
                                                onDragLeave: () => setOverUnitKey((atual) => (atual === unitKey ? null : atual)),
                                                onDrop: (e: React.DragEvent) => {
                                                    if (!dragUnit) return;
                                                    e.preventDefault();
                                                    moveUnidadeCell(dragUnit, ref);
                                                    setDragUnit(null);
                                                    setOverUnitKey(null);
                                                },
                                            };

                                            if (colIndex < row.length) {
                                                const unidade = row[colIndex];
                                                const { isValid, isPartial } = validateUnidade(unidade);
                                                let cellStyle = !isPartial ? 'bg-gray-100 text-gray-500' : (isValid ? 'bg-emerald-200 text-emerald-900' : 'bg-red-100 text-red-800');

                                                if (unidade.confirmada === false) {
                                                    cellStyle = 'bg-yellow-200 text-yellow-800';
                                                }

                                                if (isSource) cellStyle = 'bg-yellow-300';
                                                else if (isSelected) cellStyle = 'bg-blue-200';
                                                if (isDraggingThis) cellStyle += ' opacity-40';
                                                if (isDropTarget) cellStyle += ' ring-2 ring-inset ring-indigo-500';

                                                return (
                                                    <td key={colIndex} className="p-2 text-center align-middle">
                                                        <div
                                                            onClick={() => handleCellClick(!!isSource, unitKey, ref)}
                                                            draggable={!modoLinha}
                                                            onDragStart={(e) => {
                                                                if (modoLinha) return;
                                                                setDragUnit(ref);
                                                                e.dataTransfer.effectAllowed = 'move';
                                                            }}
                                                            onDragEnd={() => {
                                                                setDragUnit(null);
                                                                setOverUnitKey(null);
                                                            }}
                                                            {...dragHandlers}
                                                            title="Arraste para trocar de posição com outra unidade"
                                                            className={`group relative p-3 h-16 flex items-center justify-center rounded-md cursor-grab active:cursor-grabbing transition-all hover:shadow-lg hover:scale-105 transform ${cellStyle}`}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => handleUnitSelection(unitKey, ref)}
                                                                className="absolute top-2 left-2 h-4 w-4 z-10 rounded-full appearance-none border border-gray-300 bg-white checked:bg-indigo-600 checked:border-indigo-600 cursor-pointer transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                                                disabled={!!isSource}
                                                                onClick={e => e.stopPropagation()}
                                                            />
                                                            <Building className="h-8 w-8" />
                                                            <button type="button" className="absolute top-1 right-1 h-5 w-5 bg-red-100 text-red-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100" onClick={e => { e.stopPropagation(); removeUnidade(rowIndex, colIndex); }}>&times;</button>
                                                        </div>
                                                    </td>
                                                );
                                            }
                                            return (
                                                <td key={colIndex} className="p-2">
                                                    <div
                                                        {...dragHandlers}
                                                        title={dragUnit ? 'Soltar aqui move a unidade para esta posição' : undefined}
                                                        className={`h-16 bg-gray-50 rounded-md transition-all ${isDropTarget ? 'ring-2 ring-inset ring-indigo-500 bg-indigo-50' : ''}`}
                                                    ></div>
                                                </td>
                                            );
                                        })}
                                        <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex justify-end items-center space-x-2">
                                                <button
                                                    type="button"
                                                    className="px-2 py-1 bg-amber-500 text-white text-xs font-bold rounded-md hover:bg-amber-600 flex items-center gap-1"
                                                    onClick={() => handleSetSourceRow(rowIndex)}
                                                    title="Usar este andar inteiro como modelo para outros andares"
                                                >
                                                    <Rows3 size={12} /> Modelo
                                                </button>
                                                <button type="button" className="px-2 py-1 bg-green-500 text-white text-xs font-bold rounded-md" onClick={() => addUnidadeToAndar(rowIndex)}>+ Unidade</button>
                                                <button type="button" className={dangerButtonStyle} onClick={() => removeAndar(rowIndex)}>Remover Andar</button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ) : (
                <p className="text-sm text-gray-500 text-center py-4">Nenhum andar adicionado a esta torre. Clique em "Adicionar Novo Andar" para começar.</p>
            )}
            <div className="mt-6">
                <button type="button" onClick={addAndar} className={successButtonStyle}>Adicionar Novo Andar</button>
            </div>
        </>
    );
};

export default TorreView;
